import type { FC, ReactNode } from 'react'
import type { Project } from '@/types'

import Image from 'next/image'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { TransitionLink } from '@/components/generals/transition-link'

interface ProjectCardProps {
    project: Project
    className?: string
    imageClassName?: string
    sizes?: string
}

export const ProjectCard: FC<ProjectCardProps> = ({
    project,
    className,
    imageClassName = 'aspect-[4/3]',
    sizes = '(min-width: 1024px) 45vw, 100vw'
}): ReactNode => {
    return (
        <TransitionLink href={`/projects/${project.slug}`} className={cn('group block', className)}>
            <div className={cn('relative overflow-hidden rounded-[28px] bg-sand', imageClassName)}>
                <Image
                    src={project.cover}
                    alt={project.alt}
                    fill
                    sizes={sizes}
                    className='object-cover transition-transform duration-[1400ms] ease-expo group-hover:scale-[1.05]'
                />

                <div
                    aria-hidden='true'
                    className='absolute inset-0 bg-gradient-to-t from-ink/55 via-transparent to-transparent opacity-0 transition-opacity duration-700 group-hover:opacity-100'
                />

                <span className='absolute left-4 top-4 rounded-full bg-cream/90 px-3 py-1.5 font-mono text-[10.5px] uppercase tracking-[0.16em] text-ink backdrop-blur-md sm:left-5 sm:top-5'>
                    {project.category}
                </span>

                <span className='absolute bottom-5 right-5 inline-flex translate-y-3 items-center gap-2 rounded-full bg-cream px-4 py-2.5 text-sm font-medium text-ink opacity-0 transition-[transform,translate,scale,rotate,opacity] duration-500 ease-expo group-hover:translate-y-0 group-hover:opacity-100'>
                    See the job
                    <ArrowUpRight className='size-4' aria-hidden='true' />
                </span>
            </div>

            <div className='mt-5 flex items-start justify-between gap-6'>
                <div className='min-w-0'>
                    <p className='font-mono text-[11px] uppercase tracking-[0.16em] text-slate'>
                        {project.location}
                    </p>

                    <h3 className='mt-2 font-heading text-[1.6rem] font-semibold leading-tight tracking-[-0.03em] text-ink transition-colors group-hover:text-ember sm:text-[1.85rem]'>
                        {project.title}
                    </h3>

                    <p className='mt-2 text-[15px] text-slate'>
                        {project.summary}
                    </p>
                </div>

                <span className='mt-1 flex size-11 shrink-0 items-center justify-center rounded-full border border-ink/15 text-ink transition-[background-color,border-color,color,transform,translate,scale,rotate] duration-500 ease-expo group-hover:rotate-45 group-hover:border-ember group-hover:bg-ember group-hover:text-white'>
                    <ArrowUpRight className='size-4' aria-hidden='true' />
                </span>
            </div>
        </TransitionLink>
    )
}
